import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Client } from '@/types/shelter';
import { FileText, Plus, Edit, Save, X, Trash2, Loader2, User, Reply, ChevronDown, ChevronRight } from 'lucide-react';
import { MentionTextarea } from '@/components/MentionTextarea';
import { formatNoteText } from '@/utils/formatNoteText';


const NOTE_TYPE_CONFIG = {
  general: { label: 'General', icon: '📝', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  incident: { label: 'Incident', icon: '⚠️', color: 'bg-red-100 text-red-700 border-red-200' },
};

interface ClientNote {
  id: string;
  title: string;
  content: string;
  author: string;
  timestamp: string;
  category?: 'general' | 'incident';
  parent_note_id?: string | null;
}

interface ClientProfileNotesProps {
  client: Client;
  notes: ClientNote[];
  notesLoading: boolean;
  onAddNote: (note: { title: string; content: string; category: string; parent_note_id?: string }) => void;
  onUpdateNote: (note: { id: string; title: string; content: string; category: string }) => void;
  onDeleteNote: (noteId: string) => void;
  isAddingNote: boolean;
  isUpdatingNote: boolean;
  isDeletingNote: boolean;
}

export const ClientProfileNotes: React.FC<ClientProfileNotesProps> = ({
  client,
  notes,
  notesLoading,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  isAddingNote,
  isUpdatingNote,
  isDeletingNote
}) => {
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteCategory, setNewNoteCategory] = useState<'general' | 'incident'>('general');
  const [noteTypeFilter, setNoteTypeFilter] = useState<'all' | 'general' | 'incident'>('all');
  const [editingNote, setEditingNote] = useState<{ id: string; title: string; content: string; category: string } | null>(null);
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());

  const handleAddNote = () => {
    if (!newNoteTitle.trim() || !newNoteContent.trim()) return;
    onAddNote({
      title: newNoteTitle,
      content: newNoteContent,
      category: newNoteCategory
    });
    setNewNoteTitle('');
    setNewNoteContent('');
    setNewNoteCategory('general');
  };

  const handleReply = (parentNoteId: string) => {
    if (!replyContent.trim()) return;
    const parentNote = notes.find(n => n.id === parentNoteId);
    onAddNote({
      title: `Re: ${parentNote?.title || 'Note'}`,
      content: replyContent,
      category: parentNote?.category || 'general',
      parent_note_id: parentNoteId
    });
    setReplyContent('');
    setReplyingTo(null);
    setExpandedThreads(prev => new Set(prev).add(parentNoteId));
  };

  const toggleThread = (noteId: string) => {
    setExpandedThreads(prev => {
      const next = new Set(prev);
      if (next.has(noteId)) next.delete(noteId);
      else next.add(noteId);
      return next;
    });
  };

  const getCategoryLabel = (category?: string) => {
    if (!category) return NOTE_TYPE_CONFIG.general;
    return NOTE_TYPE_CONFIG[category as keyof typeof NOTE_TYPE_CONFIG] || NOTE_TYPE_CONFIG.general;
  };

  // Separate top-level notes and replies
  const topLevelNotes = notes.filter(note => !note.parent_note_id);
  const getReplies = (noteId: string) => notes.filter(n => n.parent_note_id === noteId);

  const filteredNotes = topLevelNotes.filter(note => 
    noteTypeFilter === 'all' || note.category === noteTypeFilter
  );

  const displayContent = (content: string) => formatNoteText(content);

  const renderNote = (note: ClientNote, isReply = false) => {
    const categoryConfig = getCategoryLabel(note.category);
    const isEditing = editingNote?.id === note.id;
    const replies = getReplies(note.id);
    const hasReplies = replies.length > 0;
    const isExpanded = expandedThreads.has(note.id);

    return (
      <div key={note.id} className={isReply ? 'ml-6 border-l-2 border-muted pl-4' : ''}>
        <Card className={`border-l-4 hover:shadow-md transition-shadow bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/50 hover:bg-amber-100/80 dark:hover:bg-amber-950/40 ${
          note.category === 'incident' ? 'border-l-destructive' :
          'border-l-amber-500'
        }`}>
          <CardContent className="p-4">
            {isEditing ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Title</Label>
                    <Input 
                      value={editingNote.title}
                      onChange={(e) => setEditingNote({ ...editingNote, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select 
                      value={editingNote.category} 
                      onValueChange={(value) => setEditingNote({ ...editingNote, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(NOTE_TYPE_CONFIG).map(([key, config]) => (
                          <SelectItem key={key} value={key}>
                            <span className="flex items-center gap-2">
                              {config.icon} {config.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Content</Label>
                  <MentionTextarea 
                    value={editingNote.content}
                    onChange={(val) => setEditingNote({ ...editingNote, content: val })}
                    className="min-h-24"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => {
                      onUpdateNote(editingNote);
                      setEditingNote(null);
                    }}
                    disabled={isUpdatingNote}
                  >
                    {isUpdatingNote ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingNote(null)}>
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="font-medium text-sm">{note.title}</h4>
                      <Badge className={`text-xs border ${categoryConfig.color}`}>
                        {categoryConfig.icon} {categoryConfig.label}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <User className="h-3 w-3" />
                      <span>{note.author}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs text-muted-foreground">
                        {new Date(note.timestamp).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(note.timestamp).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {!isReply && (
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-7 w-7"
                          onClick={() => setReplyingTo(replyingTo === note.id ? null : note.id)}
                          title="Reply"
                        >
                          <Reply className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-7 w-7"
                        onClick={() => setEditingNote({
                          id: note.id,
                          title: note.title,
                          content: note.content,
                          category: note.category || 'general'
                        })}
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => setDeleteNoteId(note.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-foreground/80 mt-2 whitespace-pre-wrap p-3 rounded-md">
                  {displayContent(note.content)}
                </p>

                {/* Thread toggle */}
                {!isReply && hasReplies && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-xs text-muted-foreground"
                    onClick={() => toggleThread(note.id)}
                  >
                    {isExpanded ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronRight className="h-3 w-3 mr-1" />}
                    {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                  </Button>
                )}

                {/* Reply form */}
                {replyingTo === note.id && (
                  <div className="mt-3 space-y-2 border-t pt-3">
                    <MentionTextarea
                      value={replyContent}
                      onChange={setReplyContent}
                      placeholder="Write a reply..."
                      className="min-h-16"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleReply(note.id)} disabled={isAddingNote || !replyContent.trim()}>
                        {isAddingNote ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Reply className="h-3 w-3 mr-1" />}
                        Reply
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setReplyingTo(null); setReplyContent(''); }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Threaded replies */}
        {!isReply && isExpanded && replies
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
          .map(reply => renderNote(reply, true))
        }
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Add Note */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            Add Note
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Title</Label>
              <Input 
                value={newNoteTitle}
                onChange={(e) => setNewNoteTitle(e.target.value)}
                placeholder="Note title"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={newNoteCategory} onValueChange={(value: any) => setNewNoteCategory(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(NOTE_TYPE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        {config.icon} {config.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Content</Label>
            <MentionTextarea 
              value={newNoteContent}
              onChange={setNewNoteContent}
              placeholder="Enter note content... Use @mentions to tag staff"
              className="min-h-24"
            />
          </div>
          <Button onClick={handleAddNote} disabled={isAddingNote || !newNoteTitle.trim() || !newNoteContent.trim()}>
            {isAddingNote ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Add Note
          </Button>
        </CardContent>
      </Card>

      {/* Notes List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Notes ({topLevelNotes.length})
            </CardTitle>
            <Select value={noteTypeFilter} onValueChange={(value: any) => setNoteTypeFilter(value)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(NOTE_TYPE_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    <span className="flex items-center gap-2">
                      {config.icon} {config.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {notesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-3">
              {filteredNotes
                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                .map(note => renderNote(note))}
              {filteredNotes.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  {noteTypeFilter === 'all' 
                    ? 'No notes yet. Add your first note above.'
                    : `No ${NOTE_TYPE_CONFIG[noteTypeFilter as keyof typeof NOTE_TYPE_CONFIG]?.label.toLowerCase()} notes found.`
                  }
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Note Confirmation */}
      <AlertDialog open={!!deleteNoteId} onOpenChange={() => setDeleteNoteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be undone.
              {notes.some(n => n.parent_note_id === deleteNoteId) && (
                <span className="block mt-1 font-medium">This will also delete all replies.</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                if (deleteNoteId) {
                  onDeleteNote(deleteNoteId);
                  setDeleteNoteId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingNote ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
